import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { motion } from 'framer-motion';
import { Lock, User, Globe, Eye, EyeOff } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';

const AdminLogin = () => {
  const { login, isAuthenticated } = useAuth();
  const { t, toggleLanguage, language, isRTL } = useLanguage();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/admin');
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      toast.error(t('يرجى ملء جميع الحقول', 'Please fill all fields'));
      return;
    }

    setLoading(true);
    try {
      await login(username, password);
      toast.success(t('تم تسجيل الدخول بنجاح', 'Login successful'));
      navigate('/admin');
    } catch (error) {
      toast.error(t('اسم المستخدم أو كلمة المرور غير صحيحة', 'Invalid username or password'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-raad-dark flex items-center justify-center p-6" dir={isRTL ? 'rtl' : 'ltr'}>
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src="/assets/logo.png" 
            alt={t('رعد القابضة', 'Raad Holding')} 
            className="h-16 w-auto object-contain mx-auto mb-4 rounded-2xl bg-white p-3 shadow-lg"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          <div className="w-16 h-16 rounded-2xl bg-gradient-raad items-center justify-center mx-auto mb-4 hidden">
            <span className="text-white font-bold text-3xl">R</span>
          </div>
          <h1 className="gradient-text text-2xl font-bold">
            {t('رعد القابضة', 'Raad Holding')}
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            {t('لوحة التحكم', 'Admin Dashboard')}
          </p>
        </div>

        {/* Login Form */}
        <div className="p-8 rounded-2xl bg-raad-card border border-white/10">
          <h2 className="text-xl font-bold text-white mb-6 text-center">
            {t('تسجيل الدخول', 'Sign In')}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="username" className="text-gray-300 mb-2 block">
                {t('اسم المستخدم', 'Username')}
              </Label>
              <div className="relative">
                <User className="absolute start-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <Input
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="bg-white/5 border-white/10 text-white ps-10"
                  placeholder={t('أدخل اسم المستخدم', 'Enter username')}
                  data-testid="admin-username-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-gray-300 mb-2 block">
                {t('كلمة المرور', 'Password')}
              </Label>
              <div className="relative">
                <Lock className="absolute start-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-white/5 border-white/10 text-white ps-10 pe-10"
                  placeholder={t('أدخل كلمة المرور', 'Enter password')}
                  data-testid="admin-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute end-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-raad hover:opacity-90"
              data-testid="admin-login-btn"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                t('تسجيل الدخول', 'Sign In')
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleLanguage}
              className="text-gray-500 hover:text-white"
            >
              <Globe className="w-4 h-4 me-2" />
              {language === 'ar' ? 'English' : 'العربية'}
            </Button>
          </div>
        </div>

        <p className="text-center text-gray-600 text-xs mt-6">
          {t('الاعتمادات الافتراضية: admin / admin', 'Default credentials: admin / admin')}
        </p>
      </motion.div>
    </div>
  );
};

export default AdminLogin;
